import React, {Fragment, useEffect, useState, useRef, useCallback} from "react";
import Breadcrumb from "../../../layout/breadcrumb";
import {Button, Card, CardBody, Col, Container, Row, Badge} from "reactstrap";
import CommonDataTable from "../../../share-components/table/commonDataTable";
import {useDispatch, useSelector} from "react-redux";
import {
    getAiReportSettings,
    getAiReportHistory,
    chatAiReport,
    confirmAiReport,
    getAiReportById,
    deleteAiReport,
    selectAiReports,
    setGenerating,
    resetConversation,
} from "./store/aiReportSlice";
import withReducer from "../../../store/withReducer";
import reportReducer from "../index";
import Select from "react-select";
import moment from "moment";
import DatePicker from "../../../share-components/ui/DateRangePicker";
import {DateRangePicker} from "rsuite";
import PreLoader from "../../../share-components/ui/preLoader";
import SweetAlert from "sweetalert2";
import {toast} from "react-toastify";
import ReportPreview from "./reportPreview";

const {afterToday} = DateRangePicker;

const selectStyles = {
    control: (base) => ({
        ...base,
        minHeight: 42,
        border: "1px solid #e2e8f0",
        backgroundColor: '#fff',
        borderRadius: 8,
        fontSize: 13,
        boxShadow: 'none',
        '&:hover': { borderColor: '#a0aec0' },
    }),
};

const dataMenu = [
    {type: 0, text: 'AI Reports'},
];

const statusBadge = (status) => {
    const map = {
        'pending': {color: 'warning', text: 'Pending'},
        'processing': {color: 'info', text: 'Processing'},
        'completed': {color: 'success', text: 'Completed'},
        'failed': {color: 'danger', text: 'Failed'},
    };
    const s = map[status] || {color: 'secondary', text: status};
    return <Badge color={s.color}>{s.text}</Badge>;
};

const tableColumns = [
    {
        name: "Report Name",
        selector: (row) => row.report_name || 'Generating...',
        sortable: true,
        left: true,
        minWidth: '180px',
    },
    {
        name: "Status",
        selector: (row) => row.status,
        sortable: true,
        center: true,
        cell: (row) => statusBadge(row.status),
    },
    {
        name: "Rows",
        selector: (row) => row.row_count != null ? row.row_count.toLocaleString() : '-',
        sortable: true,
        center: true,
    },
    {
        name: "Time",
        selector: (row) => row.execution_time_ms ? (row.execution_time_ms / 1000).toFixed(1) + 's' : '-',
        sortable: true,
        center: true,
    },
    {
        name: "Date",
        selector: (row) => moment(row.created_at).format('DD/MM/YYYY HH:mm'),
        sortable: true,
        center: true,
    },
    {
        name: "By",
        selector: (row) => row.generated_by || '-',
        sortable: false,
        center: true,
    },
];

// Lightweight markdown-to-JSX renderer for chat messages
const formatMessage = (text) => {
    if (!text) return null;
    const lines = text.split('\n');
    return lines.map((line, lineIdx) => {
        // Process inline formatting: **bold** and *italic*
        const parts = [];
        let remaining = line;
        let key = 0;

        while (remaining.length > 0) {
            // Bold: **text**
            const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
            if (boldMatch) {
                const idx = remaining.indexOf(boldMatch[0]);
                if (idx > 0) parts.push(<span key={key++}>{remaining.substring(0, idx)}</span>);
                parts.push(<strong key={key++}>{boldMatch[1]}</strong>);
                remaining = remaining.substring(idx + boldMatch[0].length);
                continue;
            }
            parts.push(<span key={key++}>{remaining}</span>);
            break;
        }

        // Wrap bullet lines
        const trimmed = line.trim();
        const isBullet = trimmed.startsWith('- ') || trimmed.match(/^\d+\.\s/);

        return (
            <React.Fragment key={lineIdx}>
                {isBullet ? (
                    <div style={{paddingLeft: 12, position: 'relative'}}>
                        {parts}
                    </div>
                ) : (
                    <>{parts}</>
                )}
                {lineIdx < lines.length - 1 && !isBullet && <br/>}
                {lineIdx < lines.length - 1 && isBullet && <div style={{height: 2}}/>}
            </React.Fragment>
        );
    });
};

const GREETING_MESSAGE = {
    role: 'assistant',
    content: "Hello! I'm your reporting assistant. Tell me what report you'd like to create and I'll help you set it up.\n\nYou can ask for things like order reports, product data, returns, refunds, and more.",
    quickReplies: ["Show me order reports", "Product stock levels", "Returns report", "Refund summary"],
};

const AiReport = () => {
    const dispatch = useDispatch();

    const TodayDate = new Date();
    const thirtyDaysAgo = new Date(new Date().setDate(TodayDate.getDate() - 30));
    const [dateRange, setDateRange] = useState([thirtyDaysAgo, TodayDate]);
    const [startDate, endDate] = dateRange;

    const [seller, setSeller] = useState(null);
    const [partner, setPartner] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);

    // Chat state
    const [messages, setMessages] = useState([GREETING_MESSAGE]);
    const [inputMessage, setInputMessage] = useState('');
    const [confirmDisabled, setConfirmDisabled] = useState(false);
    const [reportConfirmed, setReportConfirmed] = useState(false);

    const pollingRef = useRef(null);
    const chatContainerRef = useRef(null);
    const inputRef = useRef(null);

    const aiReportState = useSelector(({report}) => report.aiReport);
    const aiReports = useSelector(selectAiReports);

    useEffect(() => {
        dispatch(getAiReportSettings());
    }, [dispatch]);

    useEffect(() => {
        loadHistory();
    }, [page, pageSize, dispatch]);

    useEffect(() => {
        return () => {
            if (pollingRef.current) clearInterval(pollingRef.current);
        };
    }, []);

    // Auto-scroll only the chat container, not the page
    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages, aiReportState.chatLoading]);

    const loadHistory = () => {
        setLoading(true);
        dispatch(getAiReportHistory({page, pageSize})).then(() => setLoading(false));
    };

    const startPolling = (reportId) => {
        if (pollingRef.current) clearInterval(pollingRef.current);
        pollingRef.current = setInterval(() => {
            dispatch(getAiReportById(reportId)).then((res) => {
                if (!res.error && res.payload) {
                    const status = res.payload.status;
                    if (status === 'completed' || status === 'failed') {
                        clearInterval(pollingRef.current);
                        pollingRef.current = null;
                        dispatch(setGenerating(false));
                        loadHistory();
                        if (status === 'completed') {
                            const capMsg = res.payload.error_message;
                            if (capMsg) {
                                toast.warning('Report generated.' + capMsg, {position: toast.POSITION.TOP_RIGHT, autoClose: 8000});
                            } else {
                                toast.success('Report generated successfully!', {position: toast.POSITION.TOP_RIGHT});
                            }
                        } else {
                            toast.error(res.payload.error_message || 'Report generation failed.', {position: toast.POSITION.TOP_RIGHT});
                        }
                    }
                }
            });
        }, 3000);
    };

    const getFilters = useCallback(() => ({
        start_date: startDate ? moment(startDate).format('YYYY-MM-DD') : null,
        end_date: endDate ? moment(endDate).format('YYYY-MM-DD') : null,
        seller_id: seller ? seller.id : null,
        partner_id: partner ? partner.id : null,
    }), [startDate, endDate, seller, partner]);

    const sendMessage = (msg) => {
        if (!msg || msg.trim().length < 2) return;
        if (aiReportState.chatLoading) return;

        const trimmed = msg.trim();
        setMessages(prev => [...prev, {role: 'user', content: trimmed}]);
        setInputMessage('');

        dispatch(chatAiReport({
            message: trimmed,
            conversation_id: aiReportState.conversationId,
            filters: getFilters(),
        })).then((res) => {
            if (!res.error && res.payload && !res.payload.error) {
                const p = res.payload;
                setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: p.message,
                    isReady: p.is_ready || false,
                    reportIntent: p.report_intent || null,
                    quickReplies: p.quick_replies || [],
                }]);

                // If auto-confirmed via chat (user typed "yes" / clicked quick reply), start polling
                if (p.is_confirmed && p.report && p.report.id) {
                    setReportConfirmed(true);
                    toast.info('Report generation started...', {position: toast.POSITION.TOP_RIGHT});
                    startPolling(p.report.id);
                    dispatch(setGenerating(true));
                    loadHistory();
                }
            } else {
                const errorMsg = res.payload?.message || 'Something went wrong. Please try again.';
                setMessages(prev => [...prev, {role: 'assistant', content: errorMsg}]);
            }
        });
    };

    const handleSendMessage = () => sendMessage(inputMessage);

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleQuickReply = (text) => {
        sendMessage(text);
    };

    const handleConfirmGenerate = () => {
        if (!aiReportState.conversationId || confirmDisabled || aiReportState.generating) return;
        setConfirmDisabled(true);

        dispatch(confirmAiReport({
            conversation_id: aiReportState.conversationId,
        })).then((res) => {
            if (!res.error && res.payload && res.payload.id) {
                setReportConfirmed(true);
                setConfirmDisabled(false);
                toast.info('Report generation started...', {position: toast.POSITION.TOP_RIGHT});
                startPolling(res.payload.id);
                loadHistory();
            } else {
                setConfirmDisabled(false);
                setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: res.payload?.message || 'Failed to generate the report query. Please try again or rephrase your request.',
                }]);
            }
        });
    };

    const handleNewChat = () => {
        dispatch(resetConversation());
        setMessages([GREETING_MESSAGE]);
        setInputMessage('');
        setConfirmDisabled(false);
        setReportConfirmed(false);
        if (inputRef.current) inputRef.current.focus();
    };

    const handleView = (row) => {
        if (row.status === 'completed' || row.status === 'failed') {
            // Fetch full report data including conversation
            dispatch(getAiReportById(row.id)).then((res) => {
                if (!res.error && res.payload) {
                    setSelectedReport(res.payload);
                } else {
                    setSelectedReport(row);
                }
                setPreviewOpen(true);
            });
        } else {
            toast.info('Report is still ' + row.status + '.', {position: toast.POSITION.TOP_RIGHT});
        }
    };

    const handleDownload = (row) => {
        if (row.file_url) {
            const link = document.createElement("a");
            link.href = row.file_url;
            link.setAttribute("download", "");
            document.body.appendChild(link);
            link.click();
            link.remove();
        }
    };

    const handleDelete = (row) => {
        SweetAlert.fire({
            title: 'Are you sure?',
            text: "This will permanently delete the report and its file.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel',
            reverseButtons: true
        }).then((result) => {
            if (result.value) {
                dispatch(deleteAiReport(row.id)).then(() => {
                    toast.success('Report deleted.', {position: toast.POSITION.TOP_RIGHT});
                });
            }
        });
    };

    const actionColumns = [
        ...tableColumns,
        {
            name: 'Action',
            selector: row => (
                <div className="d-flex align-items-center" style={{gap: 6}}>
                    <a className="cursor-pointer" onClick={() => handleView(row)} title="Preview"
                       style={{color: '#4c6ef5', fontSize: 14}}>
                        <i className="fa fa-eye"></i>
                    </a>
                    {row.status === 'completed' && row.file_url && (
                        <a className="cursor-pointer" onClick={() => handleDownload(row)} title="Download"
                           style={{color: '#38a169', fontSize: 14}}>
                            <i className="fa fa-download"></i>
                        </a>
                    )}
                    <a className="cursor-pointer" onClick={() => handleDelete(row)} title="Delete"
                       style={{color: '#e53e3e', fontSize: 14}}>
                        <i className="fa fa-trash"></i>
                    </a>
                </div>
            ),
            sortable: false,
            center: true,
            minWidth: '100px',
        }
    ];

    const lastReadyMessage = messages.filter(m => m.isReady).pop();
    const showSummaryCard = lastReadyMessage && lastReadyMessage.reportIntent;

    // Find the last message's quick replies (only show for the very last assistant message)
    const lastAssistantMsg = [...messages].reverse().find(m => m.role === 'assistant');
    const activeQuickReplies = (lastAssistantMsg && lastAssistantMsg.quickReplies && lastAssistantMsg.quickReplies.length > 0
        && !aiReportState.chatLoading && !aiReportState.generating && !reportConfirmed && !confirmDisabled)
        ? lastAssistantMsg.quickReplies : [];

    return (
        <Fragment>
            <Breadcrumb title="AI Reports" data={dataMenu}/>
            <Container fluid={true} className="product-wrapper">
                <div className="product-grid">
                    <div className="feature-products">
                        {/* Chat Card */}
                        <Card className="mb-3" style={{border: 'none', boxShadow: '0 1px 8px rgba(0,0,0,0.06)', borderRadius: 12, overflow: 'hidden'}}>
                            {/* Header */}
                            <div style={{
                                padding: '14px 20px',
                                borderBottom: '1px solid #edf2f7',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                backgroundColor: '#fff',
                            }}>
                                <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                                    <div style={{
                                        width: 32, height: 32, borderRadius: 8,
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: '#fff', fontSize: 14,
                                    }}>
                                        <i className="fa fa-magic"></i>
                                    </div>
                                    <span style={{fontWeight: 600, fontSize: 15, color: '#1a202c'}}>Report Assistant</span>
                                </div>
                                <button
                                    onClick={handleNewChat}
                                    style={{
                                        border: '1px solid #e2e8f0', borderRadius: 6,
                                        padding: '5px 12px', fontSize: 12, fontWeight: 500,
                                        background: '#fff', color: '#4a5568', cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', gap: 5,
                                    }}
                                >
                                    <i className="fa fa-plus" style={{fontSize: 10}}></i> New Chat
                                </button>
                            </div>

                            <CardBody style={{padding: 0}}>
                                {/* Filters */}
                                <div style={{padding: '12px 20px', borderBottom: '1px solid #edf2f7', backgroundColor: '#f7fafc'}}>
                                    <Row className="align-items-center" style={{margin: 0}}>
                                        <Col md={4} style={{paddingLeft: 4, paddingRight: 8}}>
                                            <div style={{fontSize: 11, fontWeight: 500, color: '#718096', marginBottom: 4}}>Date Range</div>
                                            <div className="dashboard-datepicker">
                                                <DatePicker
                                                    dateRange={dateRange}
                                                    dateRangeChange={(date) => setDateRange(date)}
                                                    defaultValue={[thirtyDaysAgo, TodayDate]}
                                                    disabledDate={afterToday()}
                                                    paddingLeft={0}
                                                />
                                            </div>
                                        </Col>
                                        <Col md={4} style={{paddingLeft: 8, paddingRight: 8}}>
                                            <div style={{fontSize: 11, fontWeight: 500, color: '#718096', marginBottom: 4}}>Seller</div>
                                            <Select
                                                placeholder="All sellers"
                                                getOptionLabel={(o) => o.shop_name}
                                                getOptionValue={(o) => o.id}
                                                isClearable
                                                styles={selectStyles}
                                                components={{IndicatorSeparator: () => null}}
                                                options={aiReportState.settings.sellers}
                                                onChange={(e) => setSeller(e || null)}
                                            />
                                        </Col>
                                        <Col md={4} style={{paddingLeft: 8, paddingRight: 4}}>
                                            <div style={{fontSize: 11, fontWeight: 500, color: '#718096', marginBottom: 4}}>Partner</div>
                                            <Select
                                                placeholder="All partners"
                                                getOptionLabel={(o) => o.name}
                                                getOptionValue={(o) => o.id}
                                                isClearable
                                                styles={selectStyles}
                                                components={{IndicatorSeparator: () => null}}
                                                options={aiReportState.settings.partners}
                                                onChange={(e) => setPartner(e || null)}
                                            />
                                        </Col>
                                    </Row>
                                </div>

                                {/* Chat Messages */}
                                <div ref={chatContainerRef} style={{
                                    height: 420,
                                    overflowY: 'auto',
                                    padding: '20px 20px 12px',
                                    backgroundColor: '#f7fafc',
                                }}>
                                    {messages.map((msg, idx) => (
                                        <div key={idx} style={{
                                            display: 'flex',
                                            justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                                            marginBottom: 16,
                                        }}>
                                            {/* Assistant avatar */}
                                            {msg.role === 'assistant' && (
                                                <div style={{
                                                    width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    color: '#fff', fontSize: 11, marginRight: 8, marginTop: 2,
                                                }}>
                                                    <i className="fa fa-magic"></i>
                                                </div>
                                            )}
                                            <div style={{maxWidth: '75%'}}>
                                                <div style={{
                                                    padding: '10px 14px',
                                                    borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                                                    backgroundColor: msg.role === 'user' ? '#4c6ef5' : '#ffffff',
                                                    color: msg.role === 'user' ? '#ffffff' : '#2d3748',
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                                                    fontSize: 13,
                                                    lineHeight: 1.6,
                                                    wordBreak: 'break-word',
                                                }}>
                                                    {msg.role === 'assistant' ? formatMessage(msg.content) : msg.content}
                                                </div>
                                            </div>
                                            {/* User avatar */}
                                            {msg.role === 'user' && (
                                                <div style={{
                                                    width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                                                    backgroundColor: '#4c6ef5',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    color: '#fff', fontSize: 11, marginLeft: 8, marginTop: 2,
                                                    fontWeight: 600,
                                                }}>
                                                    <i className="fa fa-user"></i>
                                                </div>
                                            )}
                                        </div>
                                    ))}

                                    {/* Typing indicator */}
                                    {aiReportState.chatLoading && (
                                        <div style={{display: 'flex', alignItems: 'flex-start', marginBottom: 16}}>
                                            <div style={{
                                                width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                color: '#fff', fontSize: 11, marginRight: 8, marginTop: 2,
                                            }}>
                                                <i className="fa fa-magic"></i>
                                            </div>
                                            <div style={{
                                                padding: '10px 14px',
                                                borderRadius: '12px 12px 12px 2px',
                                                backgroundColor: '#fff',
                                                boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                                                fontSize: 13, color: '#a0aec0',
                                            }}>
                                                <div className="ai-typing-dots">
                                                    <span></span><span></span><span></span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Confirm card */}
                                    {showSummaryCard && (
                                        <div style={{
                                            margin: '4px 0 16px 36px',
                                            padding: '16px 18px',
                                            background: 'linear-gradient(135deg, #ebf4ff 0%, #e8f5e9 100%)',
                                            border: '1px solid #bee3f8',
                                            borderRadius: 10,
                                        }}>
                                            <div style={{fontWeight: 600, fontSize: 13, color: '#2b6cb0', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6}}>
                                                <i className="fa fa-file-text-o"></i> Report Summary
                                            </div>
                                            <div style={{fontSize: 12.5, color: '#2d3748', lineHeight: 1.7}}>
                                                {lastReadyMessage.reportIntent.purpose && (
                                                    <div style={{marginBottom: 4}}><strong>Purpose:</strong> {lastReadyMessage.reportIntent.purpose}</div>
                                                )}
                                                {lastReadyMessage.reportIntent.date_range && (
                                                    <div style={{marginBottom: 4}}><strong>Date Range:</strong> {lastReadyMessage.reportIntent.date_range.start} to {lastReadyMessage.reportIntent.date_range.end}</div>
                                                )}
                                                {lastReadyMessage.reportIntent.fields && lastReadyMessage.reportIntent.fields.length > 0 && (
                                                    <div style={{marginBottom: 4}}><strong>Includes:</strong> {lastReadyMessage.reportIntent.fields.join(', ')}</div>
                                                )}
                                                {lastReadyMessage.reportIntent.conditions && lastReadyMessage.reportIntent.conditions.length > 0 && (
                                                    <div style={{marginBottom: 4}}><strong>Conditions:</strong> {lastReadyMessage.reportIntent.conditions.join('; ')}</div>
                                                )}
                                                <div><strong>Detail:</strong> {lastReadyMessage.reportIntent.detail_level === 'summary' ? 'Summary totals' : 'Row-by-row data'}</div>
                                            </div>

                                            {/* Button / status / done states */}
                                            {!reportConfirmed && !confirmDisabled && !aiReportState.generating && (
                                                <div style={{marginTop: 14}}>
                                                    <button
                                                        onClick={handleConfirmGenerate}
                                                        style={{
                                                            background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                                                            color: '#fff', border: 'none', borderRadius: 8,
                                                            padding: '9px 22px', fontSize: 13, fontWeight: 600,
                                                            cursor: 'pointer',
                                                            display: 'flex', alignItems: 'center', gap: 6,
                                                            boxShadow: '0 2px 4px rgba(56,161,105,0.3)',
                                                        }}
                                                    >
                                                        <i className="fa fa-check-circle"></i> Confirm &amp; Generate Report
                                                    </button>
                                                </div>
                                            )}
                                            {(confirmDisabled || aiReportState.generating) && (
                                                <div style={{
                                                    marginTop: 12, padding: '8px 12px',
                                                    backgroundColor: '#fffaf0', border: '1px solid #fbd38d',
                                                    borderRadius: 6, fontSize: 12, color: '#c05621',
                                                    display: 'flex', alignItems: 'center', gap: 6,
                                                }}>
                                                    <i className="fa fa-spinner fa-spin"></i>
                                                    {confirmDisabled ? 'Preparing query...' : 'Generating report...'}
                                                </div>
                                            )}
                                            {reportConfirmed && !confirmDisabled && !aiReportState.generating && (
                                                <div style={{
                                                    marginTop: 12, padding: '8px 12px',
                                                    backgroundColor: '#f0fff4', border: '1px solid #9ae6b4',
                                                    borderRadius: 6, fontSize: 12, color: '#276749',
                                                    display: 'flex', alignItems: 'center', gap: 6,
                                                }}>
                                                    <i className="fa fa-check-circle"></i>
                                                    Report generated. Check the history below.
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Legacy generating indicator — only for non-conversation reports */}
                                    {aiReportState.generating && !showSummaryCard && (
                                        <div style={{
                                            margin: '4px 0 16px 36px',
                                            padding: '12px 16px',
                                            backgroundColor: '#fffaf0',
                                            border: '1px solid #fbd38d',
                                            borderRadius: 8,
                                            fontSize: 13, color: '#c05621',
                                            display: 'flex', alignItems: 'center', gap: 8,
                                        }}>
                                            <i className="fa fa-spinner fa-spin"></i>
                                            Your report is being generated. This may take a moment...
                                        </div>
                                    )}

                                </div>

                                {/* Quick Replies */}
                                {activeQuickReplies.length > 0 && (
                                    <div style={{
                                        padding: '8px 20px 4px',
                                        backgroundColor: '#f7fafc',
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 8,
                                    }}>
                                        {activeQuickReplies.map((qr, idx) => (
                                            <button
                                                key={idx}
                                                onClick={() => handleQuickReply(qr)}
                                                style={{
                                                    border: '1px solid #cbd5e0',
                                                    borderRadius: 20,
                                                    padding: '6px 14px',
                                                    fontSize: 12,
                                                    fontWeight: 500,
                                                    backgroundColor: '#fff',
                                                    color: '#4a5568',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.15s ease',
                                                    whiteSpace: 'nowrap',
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.backgroundColor = '#4c6ef5';
                                                    e.target.style.color = '#fff';
                                                    e.target.style.borderColor = '#4c6ef5';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.backgroundColor = '#fff';
                                                    e.target.style.color = '#4a5568';
                                                    e.target.style.borderColor = '#cbd5e0';
                                                }}
                                            >
                                                {qr}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {/* Input */}
                                <div style={{
                                    padding: '12px 20px',
                                    borderTop: '1px solid #edf2f7',
                                    backgroundColor: '#fff',
                                    display: 'flex',
                                    gap: 8,
                                    alignItems: 'center',
                                }}>
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        placeholder="Describe the report you need..."
                                        value={inputMessage}
                                        onChange={(e) => setInputMessage(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        disabled={aiReportState.chatLoading || aiReportState.generating}
                                        style={{
                                            flex: 1, border: '1px solid #e2e8f0', borderRadius: 8,
                                            padding: '10px 14px', fontSize: 13, outline: 'none',
                                            backgroundColor: (aiReportState.chatLoading || aiReportState.generating) ? '#f7fafc' : '#fff',
                                        }}
                                    />
                                    <button
                                        onClick={handleSendMessage}
                                        disabled={aiReportState.chatLoading || aiReportState.generating || !inputMessage.trim()}
                                        style={{
                                            width: 40, height: 40, borderRadius: 8, border: 'none',
                                            background: (!inputMessage.trim() || aiReportState.chatLoading || aiReportState.generating)
                                                ? '#e2e8f0' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            color: '#fff', fontSize: 14,
                                            cursor: (!inputMessage.trim() || aiReportState.chatLoading || aiReportState.generating) ? 'not-allowed' : 'pointer',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <i className="fa fa-paper-plane"></i>
                                    </button>
                                </div>
                            </CardBody>
                        </Card>

                        {/* History */}
                        <Card style={{border: 'none', boxShadow: '0 1px 8px rgba(0,0,0,0.06)', borderRadius: 12}}>
                            <CardBody>
                                <h6 style={{fontWeight: 600, fontSize: 15, marginBottom: 16}}>Report History</h6>
                                {loading ? (
                                    <div style={{minHeight: 150}}><PreLoader/></div>
                                ) : (
                                    <div className="table-responsive product-table">
                                        <CommonDataTable
                                            headerColumns={actionColumns}
                                            gridData={aiReports}
                                            noAction
                                            paginationServer
                                            paginationTotalRows={aiReportState.tableRowCount}
                                            onChangePage={(page) => setPage(page)}
                                            onChangeRowsPerPage={(ps) => setPageSize(ps)}
                                        />
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </div>
                </div>
            </Container>

            <ReportPreview
                isOpen={previewOpen}
                toggle={() => setPreviewOpen(false)}
                report={selectedReport}
                dbName={aiReportState.settings.db_name || ''}
            />

            <style>{`
                .ai-typing-dots {
                    display: flex;
                    gap: 4px;
                    padding: 4px 0;
                }
                .ai-typing-dots span {
                    width: 7px;
                    height: 7px;
                    border-radius: 50%;
                    background-color: #a0aec0;
                    animation: aiTypingBounce 1.4s infinite ease-in-out both;
                }
                .ai-typing-dots span:nth-child(1) { animation-delay: -0.32s; }
                .ai-typing-dots span:nth-child(2) { animation-delay: -0.16s; }
                .ai-typing-dots span:nth-child(3) { animation-delay: 0s; }
                @keyframes aiTypingBounce {
                    0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
                    40% { transform: scale(1); opacity: 1; }
                }
            `}</style>
        </Fragment>
    );
};

export default withReducer([{report: reportReducer}])(AiReport);
